import React from 'react';
import { Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import { PointTag, TennisGame, TennisPoint } from '../../utils/tennisScore';
import { BoardPlayer, minutesSeconds, pairText } from './tennisBoard';

interface TennisGamePointsProps {
  players: [BoardPlayer, BoardPlayer];
  /** A finished game, or the points of the game in play. */
  game?: TennisGame;
  current?: { points: TennisPoint[]; label: string; serverId?: number; stopped: boolean };
}

const TAGS: Record<PointTag, { short: string; long: string }> = {
  BREAK_POINT: { short: 'BP', long: 'Break point' },
  SET_POINT: { short: 'SP', long: 'Set point' },
  MATCH_POINT: { short: 'MP', long: 'Match point' },
};

const playerOf = (players: [BoardPlayer, BoardPlayer], id?: number) => players.find(p => p.competitor.id === id);

/**
 * One game point by point: the score after each point in the winner's colour, break, set and match
 * points above — struck through when saved — and in a tiebreak who served each point.
 */
export const TennisGamePoints: React.FC<TennisGamePointsProps> = ({ players, game, current }) => {
  const points = game?.pointLog ?? current?.points ?? [];
  const name = (id?: number) => playerOf(players, id)?.competitor.name ?? 'unknown';

  let title: string;
  let detail: string;
  if (game) {
    const how = game.tiebreak
      ? `${name(game.winnerCompetitorId)} won the tiebreak ${pairText(game.wonFrom)}`
      : game.breakOfServe
        ? `${name(game.winnerCompetitorId)} broke from ${pairText(game.wonFrom)}`
        : `${name(game.serverCompetitorId)} held from ${pairText(game.wonFrom)}`;
    const took = points.length > 1 ? ` · ${minutesSeconds(points[points.length - 1].timestamp - points[0].timestamp)}` : '';
    title = `Set ${game.set} · ${game.tiebreak ? 'Tiebreak' : `Game ${game.game}`}`;
    detail = `${name(game.serverCompetitorId)} served${game.tiebreak ? ' first' : ''} · ${how} · ${points.length} points${took}`;
  } else if (current) {
    title = `${current.label} · ${current.stopped ? 'not finished' : 'in play'}`;
    detail = current.stopped
      ? `the match stopped after ${points.length} point${points.length === 1 ? '' : 's'} of it`
      : `${name(current.serverId)} serving · ${points.length} point${points.length === 1 ? '' : 's'} so far`;
  } else {
    return null;
  }

  return (
    <Box borderTopWidth="1px" pt={3} mt={1}>
      <Flex gap={3} align="baseline" flexWrap="wrap" mb={2}>
        <Text fontWeight="bold" fontSize="sm">{title}</Text>
        <Text fontSize="sm" color="gray.600">{detail}</Text>
      </Flex>
      {points.length === 0 ? (
        <Text fontSize="sm" color="gray.500">No point played in this game yet.</Text>
      ) : (
        <Flex gap={1.5} flexWrap="wrap" align="flex-end">
          {points.map((point, index) => {
            const winner = playerOf(players, point.winnerCompetitorId);
            const scheme = winner?.scheme ?? 'gray';
            const tag = point.tag ? TAGS[point.tag] : undefined;
            const saved = point.tag !== undefined && point.tagFor !== point.winnerCompetitorId;
            return (
              <React.Fragment key={point.sequence}>
                {index > 0 && <Text color="gray.400" pb={point.tiebreak ? 5 : 1.5}>›</Text>}
                <Flex direction="column" align="center" gap="2px">
                  <Tooltip label={tag ? `${tag.long} for ${name(point.tagFor)}${saved ? ', saved' : ''}` : undefined}>
                    <Text fontSize="2xs" fontWeight="extrabold" h="12px" lineHeight="12px"
                          color={saved ? 'gray.500' : 'red.600'} textDecoration={saved ? 'line-through' : undefined}>
                      {tag?.short ?? ''}
                    </Text>
                  </Tooltip>
                  <Box minW="54px" textAlign="center" px={1.5} py={1} borderRadius="md" borderWidth="1px"
                       borderColor={`${scheme}.500`} bg={`${scheme}.100`} color={`${scheme}.800`}
                       fontSize="sm" fontWeight={point.score ? 'semibold' : 'extrabold'}>
                    {point.score ? pairText(point.score) : `Game ${winner?.competitor.name ?? ''}`}
                  </Box>
                  {point.tiebreak && (
                    <Text fontSize="2xs" color="gray.500" noOfLines={1}>{name(point.serverCompetitorId)} srv</Text>
                  )}
                </Flex>
              </React.Fragment>
            );
          })}
        </Flex>
      )}
    </Box>
  );
};

export default TennisGamePoints;
